import { Component, OnInit, OnChanges, SimpleChanges, TemplateRef, ViewChild, AfterViewInit, ChangeDetectorRef, Input, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule, AbstractControl } from '@angular/forms';
import { HeaderComponent } from '../../header/header.component';
import { PageHeaderComponent } from '../../page-header/page-header.component';
import { ProviderSidebarComponent } from '../../../../provider/sidebar/provider-sidebar.component';
import { OrgAdminSidebarComponent } from '../../../../org-admin/sidebar/org-admin-sidebar.component';
import { ModalComponent } from '../../primeng/modal/modal.component';
import { StepperComponent, StepperStep } from '../../primeng';
import { YearRangePickerComponent } from '../../primeng/year-range-picker/year-range-picker.component';
import { CatalogueApiService, AuthApiService } from '@angular-workspace/services';
import { ToasterService } from '../../../../services/toaster.service';
import {
  DATASET_TYPE_OPTIONS,
  FileFormatConfigService,
  getDatasetTypeDisplayLabel,
} from '../../../services/file-format-config.service';
import { FormatService } from '../../../services/format.service';
import {
  buildAssetCataloguePayloadWithAuth,
  AssetDescriptorInput,
  resolveModifyAssetDescriptors,
} from '../../../utils/asset-catalogue-payload.util';
import { environment } from '../../../../../environments/environment';

export interface DatasetData {
  title: string;
  permission: 'Open' | 'Restricted' | 'Private';
  metadataOption: 'enter' | 'upload' | null;
}

@Component({
  selector: 'app-provider-preview-metadata',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    HeaderComponent,
    ProviderSidebarComponent,
    OrgAdminSidebarComponent,
    PageHeaderComponent,
    StepperComponent,
    YearRangePickerComponent,
    ModalComponent,
  ],
  templateUrl: './preview-metadata.component.html',
  styleUrl: './preview-metadata.component.scss',
})
export class ProviderPreviewMetadataComponent implements OnInit, OnChanges, AfterViewInit {

  get isOrgAdminRoute(): boolean {
    return this.router.url.includes('/org-admin/');
  }

  currentStep = 2; // Step 3 (0-based index) - Preview Metadata
  totalSteps = 4;

  @ViewChild('step3Template', { static: false }) step3Template!: TemplateRef<any>;

  // Inputs for editable mode
  @Input() editableMode: boolean = false;
  @Input() assetData: any = null;
  @Input() assetType: 'dataset' | 'model' | null = null;
  /** Modify-asset modal: permission dropdown, read-only resource server, relaxed validation on save. */
  @Input() allowSaveWithoutDirty: boolean = false;

  /** Serialized metadata state when the modify modal opened (for change detection). */
  private initialModifySnapshot: string | null = null;

  // Outputs for form submission
  @Output() formSubmit = new EventEmitter<any>();
  @Output() formCancel = new EventEmitter<void>();

  stepperSteps: StepperStep[] = [
    {
      key: 'step1',
      label: 'Setup your datasets',
    },
    {
      key: 'step2',
      label: 'Upload metadata',
    },
    {
      key: 'step3',
      label: 'Preview Metadata',
    },
    {
      key: 'step4',
      label: 'Upload files',
    },
  ];

  /** Step 1 label from route: "Setup your Models" on add-model, "Setup your datasets" on add-dataset */
  get stepperStepsWithLabel(): StepperStep[] {
    const label = this.router.url.includes('add-model') ? 'Setup your Models' : 'Setup your datasets';
    return this.stepperSteps.map((step, i) => i === 0 ? { ...step, label } : step);
  }

  /** Hide upload step when Data Source URL is provided (publish from preview instead). */
  get filteredStepperSteps(): StepperStep[] {
    const steps = this.stepperStepsWithLabel;
    return this.hasRedirectUrl()
      ? steps.filter((step) => step.key !== 'step4')
      : steps;
  }

  isSuccessModalOpen = false;
  createdItemId: string | null = null;

  datasetData: DatasetData | null = null;
  metadataForm!: FormGroup;
  additionalInfoForm!: FormGroup;

  // Metadata fields
  parsedMetadata: any = {};

  // Additional info fields
  parsedAdditionalInfo: any = {};

  // Cached data descriptors for template access
  dataDescriptorsList: any[] = [];

  // Flag to prevent duplicate API calls
  isCreatingItem = false;

  // Tag input for editable mode
  tagInput: string = '';

  // Dropdown open state (editable mode)
  isPermissionDropdownOpen = false;
  isResourceServerDropdownOpen = false;
  isUploadFrequencyDropdownOpen = false;
  isLicenseDropdownOpen = false;
  isIndustryDropdownOpen = false;
  isDatasetTypeDropdownOpen = false;
  isFileFormatDropdownOpen = false;

  // Options for dropdowns (same as in metadata and additional-info components)
  licenseOptions: Array<{ value: string; label: string }> = [
    { value: 'Open Government License, India', label: 'Open Government License, India' },
    { value: 'CC-BY 4.0', label: 'CC-BY 4.0' },
    { value: 'Other', label: 'Other' }
  ];

  industryOptions: Array<{ value: string; label: string }> = [
    { value: 'Agriculture', label: 'Agriculture' },
    { value: 'forest', label: 'Forest' },
    { value: 'Water', label: 'Water' },
    { value: 'Energy', label: 'Energy' },
    { value: 'Transport', label: 'Transport' },
    { value: 'Health', label: 'Health' },
    { value: 'Education', label: 'Education' },
    { value: 'Other', label: 'Other' }
  ];

  resourceServerOptions: Array<{ value: string; label: string; displayLabel: string; name: string; url: string }> = [];

  /** Sentinel form value for catalogue file resource servers (no URL). */
  private static readonly FILE_RESOURCE_SERVER_VALUE = 'FILE';

  /** Preserved from API when asset uses a file resource server. */
  private cachedFileResourceServerEntry: Record<string, unknown> | null = null;

  uploadFrequencyOptions: Array<{ value: string; label: string }> = [
    { value: 'OneTime', label: 'OneTime' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'Quarterly', label: 'Quarterly' },
    { value: 'semi-annually', label: 'Semi-Annually' },
    { value: 'Annually', label: 'Annually' }
  ];

  datasetTypeOptions: Array<{ value: string; label: string }> = [...DATASET_TYPE_OPTIONS];

  fileFormatOptions: Array<{ value: string; label: string }> = [];

  private fileFormatMap: { [key: string]: Array<{ value: string; label: string }> } = {};
  private modelFileFormats: Array<{ value: string; label: string }> = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private catalogueApiService: CatalogueApiService,
    private authApiService: AuthApiService,
    private toasterService: ToasterService,
    private fileFormatConfigService: FileFormatConfigService
  ) { }

  /**
   * Get the localStorage key based on current route
   */
  private getLocalStorageKey(): string {
    const currentUrl = this.router.url;
    if (currentUrl.includes('/add-model/')) {
      return 'modelFormData';
    }
    return 'datasetFormData'; // Default to dataset
  }

  async ngOnInit(): Promise<void> {
    // ─── PHASE 1: synchronous (runs before any await) ─────────────────────
    if (this.editableMode && this.assetData) {
      // Populate permission/title immediately so modify modal dropdown reflects API values
      // before async file-format config resolves (checkbox UI uses class bindings).
      this.populateFromAssetData(this.assetData);
    } else {
      this.parseStorageDataSync();
      this.initializeResourceServerOptions();
      this.initializeForms();
      this.setupSampleDataUrlListener();
    }

    // ─── PHASE 2: async (file-format config) ──────────────────────────────
    this.fileFormatMap = await this.fileFormatConfigService.loadDatasetFileFormatMap();
    this.modelFileFormats = await this.fileFormatConfigService.getModelFileFormats();

    // ─── PHASE 3: post-async finalization ─────────────────────────────────
    if (this.editableMode && this.assetData) {
      await this.initializeEditableFromAssetData();
      return;
    }

    // Now that fileFormatMap is loaded, refresh the file-format dropdown options.
    if (this.parsedAdditionalInfo?.datasetType) {
      this.updateFileFormatOptions(this.parsedAdditionalInfo.datasetType);
    } else if (this.isModel()) {
      this.updateFileFormatOptions('');
    }
    this.syncFileFormatWithOptions();

    if (!this.dataDescriptorsList) {
      this.dataDescriptorsList = [];
    }

    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.editableMode || !this.assetData) {
      return;
    }
    const assetDataChange = changes['assetData'];
    const assetTypeChange = changes['assetType'];
    if (
      (!assetDataChange || assetDataChange.firstChange) &&
      (!assetTypeChange || assetTypeChange.firstChange)
    ) {
      return;
    }
    void this.initializeEditableFromAssetData();
  }

  /** Load modify-modal form from assetData (re-runs when asset inputs change). */
  private async initializeEditableFromAssetData(): Promise<void> {
    this.initializeResourceServerOptions();
    if (this.isModel()) {
      await this.loadModelTypeOptionsForModify();
      const modelType =
        this.assetData?.modelType ||
        this.assetData?.model_type ||
        this.assetData?.datasetType ||
        '';
      this.ensureModelTypeOptionPresent(modelType);
    }
    this.populateFromAssetData(this.assetData);
    this.initializeForms();
    if (this.parsedAdditionalInfo?.datasetType || this.isModel()) {
      this.updateFileFormatOptions(this.parsedAdditionalInfo?.datasetType ?? '');
      this.syncFileFormatWithOptions();
    }
    this.captureModifySnapshot();
    this.closeAllEditDropdowns();
    this.cdr.detectChanges();
  }

  /**
   * Synchronously read and parse data from localStorage.
   * Called at the very start of ngOnInit (before any awaits) so that
   * dataDescriptorsList is populated before ngAfterViewInit fires and
   * injects the step template into the stepper.
   */
  private parseStorageDataSync(): void {
    const localStorageKey = this.getLocalStorageKey();
    const savedData = localStorage.getItem(localStorageKey);
    if (!savedData) {
      this.dataDescriptorsList = [];
      return;
    }

    try {
      const parsed = JSON.parse(savedData);

      this.datasetData = {
        title: parsed.title || '',
        permission: parsed.permission || 'Open',
        metadataOption: parsed.metadataOption || null
      };

      // Resolve the source object: uploaded-JSON flow stores the raw JSON as jsonSchema string.
      let src: any = parsed;
      if (parsed.jsonSchema) {
        try {
          src = typeof parsed.jsonSchema === 'string'
            ? JSON.parse(parsed.jsonSchema)
            : parsed.jsonSchema;
        } catch (e) {
          console.error('Error parsing jsonSchema:', e);
          src = parsed;
        }
      }

      this.parsedMetadata = {
        shortDescription: src.shortDescription || '',
        longDescription: src.longDescription || src.description || '',
        license: src.license || '',
        otherLicense: src.otherLicense || '',
        resourceServer: src.resourceServer || '',
        uploadFrequency: src.uploadFrequency || '',
        yearRange: src.yearRange || null,
        geoCoverage: src.geoCoverage || '',
        industry: src.industry || ''
      };

      // Extract descriptors with a single, direct pass – no double-cloning.
      const rawDescriptors = this.extractDescriptorsFromSource(src);

      this.parsedAdditionalInfo = {
        datasetType: src.datasetType || '',
        fileFormat: src.fileFormat || '',
        tags: Array.isArray(src.tags) ? src.tags : [],
        sampleDataUrl: src.sampleDataUrl || '',
        dataDescriptors: rawDescriptors
      };

      this.dataDescriptorsList = rawDescriptors;
    } catch (error) {
      console.error('Error parsing saved data:', error);
      localStorage.removeItem(this.getLocalStorageKey());
      this.dataDescriptorsList = [];
    }
  }

  /**
   * Extract the dataDescriptors array from a JSON source object.
   * Supports both array format (dataDescriptors / data_descriptors) and
   * object-map format (dataDescriptor).
   */
  private extractDescriptorsFromSource(src: any): AssetDescriptorInput[] {
    if (!src || typeof src !== 'object') return [];

    // Array format: dataDescriptors or data_descriptors
    const arraySource =
      (Array.isArray(src.dataDescriptors) ? src.dataDescriptors : null) ??
      (Array.isArray(src.data_descriptors) ? src.data_descriptors : null) ??
      (Array.isArray(src.dataDescriptor) ? src.dataDescriptor : null);

    if (arraySource) {
      const result: AssetDescriptorInput[] = [];
      for (const d of arraySource) {
        const normalized = this.normalizeDataDescriptor(d);
        if (normalized) {
          result.push(normalized);
        }
      }
      return result;
    }

    // Object-map format on dataDescriptors (from mapped preview data)
    if (
      src.dataDescriptors &&
      typeof src.dataDescriptors === 'object' &&
      !Array.isArray(src.dataDescriptors)
    ) {
      const result: AssetDescriptorInput[] = [];
      for (const [key, val] of Object.entries(src.dataDescriptors)) {
        if (!val || typeof val !== 'object') continue;
        const normalized = this.normalizeDataDescriptor({ attributeName: key, ...(val as object) });
        if (normalized) {
          result.push(normalized);
        }
      }
      if (result.length > 0) {
        return result;
      }
    }

    // Object-map format: { cropType: { parameterType: '...', schemaType: '...' } }
    const mapSource = src.dataDescriptor ?? src.data_descriptor;
    if (mapSource && typeof mapSource === 'object' && !Array.isArray(mapSource)) {
      const result: AssetDescriptorInput[] = [];
      for (const [key, val] of Object.entries(mapSource)) {
        if (!val || typeof val !== 'object') continue;
        const normalized = this.normalizeDataDescriptor({ attributeName: key, ...(val as object) });
        if (normalized) {
          result.push(normalized);
        }
      }
      return result;
    }

    return [];
  }

  private stripAdexPrefix(value: string): string {
    const trimmed = value.trim();
    return /^adex:/i.test(trimmed) ? trimmed.replace(/^adex:/i, '') : trimmed;
  }

  /**
   * Normalize a descriptor object to AssetDescriptorInput.
   * Accepts camelCase / snake_case, template keys, and API-shaped entries (type, dataSchema).
   */
  private normalizeDataDescriptor(desc: any): AssetDescriptorInput | null {
    if (!desc || typeof desc !== 'object' || Array.isArray(desc)) return null;
    const attributeNameRaw = desc.attributeName ?? desc.attribute_name ?? desc.name ?? desc.label ?? '';
    const parameterTypeRaw =
      desc.parameterType ??
      desc.parameter_type ??
      desc.ParameterType ??
      (Array.isArray(desc.type) ? desc.type[0] : typeof desc.type === 'string' ? desc.type : '');
    const schemaTypeRaw =
      desc.schemaType ??
      desc.schema_type ??
      desc.dataType ??
      desc.datatype ??
      desc.dataSchema ??
      desc.data_schema ??
      '';

    const attributeName = String(attributeNameRaw ?? '').trim();
    const parameterType = this.stripAdexPrefix(String(parameterTypeRaw ?? '').trim());
    const schemaType =
      schemaTypeRaw != null && schemaTypeRaw !== ''
        ? this.stripAdexPrefix(String(schemaTypeRaw).trim())
        : '';
    const description = String(desc.description ?? '').trim();
    const unitCode = String(desc.unitCode ?? desc.unit_code ?? '').trim();
    const unitText = String(desc.unitText ?? desc.unit_text ?? '').trim();

    if (!attributeName || !parameterType || !schemaType) return null;

    const normalized: AssetDescriptorInput = { attributeName, parameterType, schemaType };
    if (description) {
      normalized.description = description;
    }
    if (unitCode) {
      normalized.unitCode = unitCode;
    }
    if (unitText) {
      normalized.unitText = unitText;
    }
    return normalized;
  }

  /** Clone normalized descriptor rows so the reactive form cannot mutate preview list items by reference. */
  private cloneDescriptorRows(rows: any[]): AssetDescriptorInput[] {
    if (!Array.isArray(rows)) return [];
    return rows
      .map((r) => this.normalizeDataDescriptor(r))
      .filter((r): r is AssetDescriptorInput => r != null);
  }

  /**
   * Build dataDescriptors array from JSON: supports dataDescriptors (array) or dataDescriptor (object map).
   */
  private parseDataDescriptorsFromJson(jsonData: any): AssetDescriptorInput[] {
    if (!jsonData || typeof jsonData !== 'object') return [];
    const out: AssetDescriptorInput[] = [];

    const asList =
      (Array.isArray(jsonData.dataDescriptors) && jsonData.dataDescriptors) ||
      (Array.isArray(jsonData.data_descriptors) && jsonData.data_descriptors) ||
      (Array.isArray(jsonData.dataDescriptor) && jsonData.dataDescriptor) ||
      null;

    if (asList) {
      asList.forEach((desc: any) => {
        const normalized = this.normalizeDataDescriptor(desc);
        if (normalized) out.push(normalized);
      });
    }

    if (out.length === 0) {
      const mapRaw = jsonData.dataDescriptor ?? jsonData.data_descriptor;
      if (mapRaw && typeof mapRaw === 'object' && !Array.isArray(mapRaw)) {
        Object.entries(mapRaw).forEach(([key, val]: [string, any]) => {
          const normalized = this.normalizeDataDescriptor(
            val && typeof val === 'object' ? { attributeName: key, ...val } : null,
          );
          if (normalized) out.push(normalized);
        });
      }
    }
    return out;
  }

  private extractResourceServerEntry(raw: unknown): Record<string, unknown> | null {
    if (!raw) {
      return null;
    }
    if (Array.isArray(raw) && raw.length > 0) {
      const first = raw[0];
      return first && typeof first === 'object' && !Array.isArray(first)
        ? (first as Record<string, unknown>)
        : null;
    }
    if (typeof raw === 'object') {
      return raw as Record<string, unknown>;
    }
    return null;
  }

  private isFileResourceServerEntry(entry: Record<string, unknown> | null): boolean {
    if (!entry) {
      return false;
    }
    const type = String(entry['type'] ?? '').trim().toLowerCase();
    const name = String(entry['name'] ?? '').trim().toUpperCase();
    return type === 'file' || name === 'FILE';
  }

  /**
   * Normalize resourceServer from API (object/array/string) to option value string
   */
  private normalizeResourceServerValue(raw: any): string {
    if (!raw) return '';
    const opts = this.resourceServerOptions || [];
    const entry = this.extractResourceServerEntry(raw);
    if (entry && this.isFileResourceServerEntry(entry)) {
      this.cachedFileResourceServerEntry = { ...entry };
      return ProviderPreviewMetadataComponent.FILE_RESOURCE_SERVER_VALUE;
    }
    this.cachedFileResourceServerEntry = null;

    if (typeof raw === 'string') {
      if (raw.toUpperCase() === ProviderPreviewMetadataComponent.FILE_RESOURCE_SERVER_VALUE) {
        return ProviderPreviewMetadataComponent.FILE_RESOURCE_SERVER_VALUE;
      }
      const found = opts.find(opt => opt.value === raw || opt.url === raw);
      return found ? found.value : raw;
    }
    if (Array.isArray(raw) && raw.length > 0) {
      const first = raw[0];
      if (typeof first === 'string') {
        const found = opts.find(opt => opt.value === first || opt.url === first);
        return found ? found.value : first;
      }
      const url = String(first?.url ?? first?.value ?? '').trim();
      const name = String(first?.name ?? '').trim();
      const found = opts.find(
        (opt) => opt.value === url || opt.url === url || (name && opt.name === name),
      );
      return found ? found.value : url || '';
    }
    if (typeof raw === 'object') {
      const url = String(raw.url ?? raw.value ?? '').trim();
      const name = String(raw.name ?? '').trim();
      const found = opts.find(
        (opt) => opt.value === url || opt.url === url || (name && opt.name === name),
      );
      return found ? found.value : url || '';
    }
    return '';
  }

  private removeProtocol(url: string): string {
    return url.replace(/^https?:\/\//, '');
  }

  /**
   * Initialize resource server options from environment (same as metadata step).
   */
  private initializeResourceServerOptions(): void {
    const fallback = [
      { value: 'http://v2.dev.ogc.iudx.io/', name: 'OGC', url: 'v2.dev.ogc.iudx.io/', label: 'OGC (v2.dev.ogc.iudx.io/)', displayLabel: 'OGC (v2.dev.ogc.iudx.io/)' },
      { value: 'https://v2.dev.rs.iudx.io/', name: 'NGSI-LD', url: 'v2.dev.rs.iudx.io/', label: 'NGSI-LD (v2.dev.rs.iudx.io/)', displayLabel: 'NGSI-LD (v2.dev.rs.iudx.io/)' },
      { value: 'https://v2.dev.rs-proxy.iudx.io/', name: 'GATEWAY', url: 'v2.dev.rs-proxy.iudx.io/', label: 'GATEWAY (v2.dev.rs-proxy.iudx.io/)', displayLabel: 'GATEWAY (v2.dev.rs-proxy.iudx.io/)' },
      {
        value: ProviderPreviewMetadataComponent.FILE_RESOURCE_SERVER_VALUE,
        name: 'FILE',
        url: '',
        label: 'FILE',
        displayLabel: 'FILE',
      },
    ];
    try {
      const env = environment as {
        ogcApiPath?: string;
        ogcMapsResourceServerUrl?: string;
        ngsiLdApiPath?: string;
        gatewayApiPath?: string;
      };
      const resourceServers = [
        { name: 'OGC', url: env.ogcMapsResourceServerUrl || env.ogcApiPath || '' },
        { name: 'NGSI-LD', url: env.ngsiLdApiPath || '' },
        { name: 'GATEWAY', url: env.gatewayApiPath || '' }
      ];
      this.resourceServerOptions = resourceServers
        .filter((server) => server.url && server.url.trim() !== '')
        .map((server) => ({
          value: server.url,
          name: server.name,
          url: this.removeProtocol(server.url),
          label: `${server.name} (${this.removeProtocol(server.url)})`,
          displayLabel: `${server.name} (${this.removeProtocol(server.url)})`
        }));
      if (this.resourceServerOptions.length === 0) {
        this.resourceServerOptions = fallback;
      }
    } catch {
      this.resourceServerOptions = fallback;
    }
  }

  /** Catalogue placeholder media URLs — not real data sources. */
  private isPlaceholderMediaUrl(url: unknown): boolean {
    const normalized = String(url ?? '').trim().toLowerCase().replace(/\/+$/, '');
    return !normalized || normalized === 'https://iudx.io' || normalized === 'http://iudx.io';
  }

  /** Return a displayable data-source URL, or empty when the API sent a placeholder. */
  private normalizeMediaUrlForDisplay(url: unknown): string {
    return this.isPlaceholderMediaUrl(url) ? '' : String(url ?? '').trim();
  }

  /** Load Model Type options dynamically (same source as model onboarding). */
  private async loadModelTypeOptionsForModify(): Promise<void> {
    try {
      const searchCriteria = [{ searchType: 'term', field: 'type', values: ['adex:AiModel'] }];
      const response = await this.catalogueApiService.getFilterList(searchCriteria as any, ['modelType']);
      const values = this.extractStringValuesFromFilterResponse(response, 'modelType');
      if (!values.length) {
        return;
      }
      const sorted = values
        .map((v) => String(v).trim())
        .filter((v) => v.length > 0)
        .sort((a, b) => a.localeCompare(b));
      this.datasetTypeOptions = sorted.map((v) => ({ value: v, label: v }));
    } catch (error) {
      console.warn('Failed to load Model Type options for modify asset:', error);
    }
  }

  private extractStringValuesFromFilterResponse(response: any, field: string): string[] {
    const uniq = (arr: any[]) => [...new Set(arr)];
    const pick = (obj: any): any => (obj ? obj[field] : undefined);

    let data: any = response;
    if (response?.result) {
      if (Array.isArray(response.result) && response.result.length > 0) {
        data = response.result[0];
      } else if (typeof response.result === 'object') {
        data = response.result;
      }
    }

    const raw = pick(data);
    if (!raw) return [];

    if (Array.isArray(raw)) {
      return uniq(raw).filter((v) => typeof v === 'string' && v.trim().length > 0);
    }

    if (typeof raw === 'string' && raw.trim().length > 0) {
      return [raw.trim()];
    }

    return [];
  }

  private ensureModelTypeOptionPresent(value: string): void {
    const v = String(value || '').trim();
    if (!v) return;
    const exists = this.datasetTypeOptions.some(
      (opt) => String(opt.value).trim().toLowerCase() === v.toLowerCase()
    );
    if (!exists) {
      this.datasetTypeOptions = [{ value: v, label: v }, ...this.datasetTypeOptions];
    }
  }

  /** Map API / mapped permission values to modify-form dropdown values (Open | Restricted). */
  private normalizeEditPermission(raw: unknown): 'Open' | 'Restricted' {
    if (raw === 'Open' || raw === 'Restricted') {
      return raw;
    }
    const upper = String(raw ?? '').trim().toUpperCase();
    if (upper === 'RESTRICTED' || upper === 'PRIVATE') {
      return 'Restricted';
    }
    if (upper === 'OPEN' || upper === 'PUBLIC') {
      return 'Open';
    }
    return 'Open';
  }

  isPermissionOptionSelected(value: 'Open' | 'Restricted'): boolean {
    if (!this.datasetData?.permission) {
      return false;
    }
    return this.normalizeEditPermission(this.datasetData.permission) === value;
  }

  /** True when the asset response includes a real media / data-source URL. */
  hasAssetMediaUrl(): boolean {
    const raw =
      this.assetData?.mediaURL ??
      this.assetData?.media_url ??
      this.assetData?.mediaUrl ??
      this.assetData?.sampleDataUrl ??
      this.parsedAdditionalInfo?.sampleDataUrl ??
      '';
    return !this.isPlaceholderMediaUrl(raw);
  }

  /** Permission is editable in modify modal only when no media URL is set. */
  isPermissionFieldEditable(): boolean {
    return this.allowSaveWithoutDirty && !this.hasAssetMediaUrl();
  }

  /** Close all custom dropdown panels in editable modify mode. */
  private closeAllEditDropdowns(): void {
    this.isPermissionDropdownOpen = false;
    this.isResourceServerDropdownOpen = false;
    this.isUploadFrequencyDropdownOpen = false;
    this.isLicenseDropdownOpen = false;
    this.isIndustryDropdownOpen = false;
    this.isDatasetTypeDropdownOpen = false;
    this.isFileFormatDropdownOpen = false;
  }

  /** Toggle permission dropdown and select options in modify (editable) mode. */
  togglePermissionDropdown(): void {
    if (!this.editableMode || !this.isPermissionFieldEditable()) return;
    const willOpen = !this.isPermissionDropdownOpen;
    this.closeAllEditDropdowns();
    this.isPermissionDropdownOpen = willOpen;
  }

  selectPermissionOption(value: 'Open' | 'Restricted'): void {
    if (!this.editableMode || !this.isPermissionFieldEditable() || !this.datasetData) return;
    this.datasetData.permission = value;
    this.isPermissionDropdownOpen = false;
    if (!this.isDataSourceUrlEnabled()) {
      this.additionalInfoForm?.get('sampleDataUrl')?.setValue('', { emitEvent: false });
    }
    this.cdr.detectChanges();
  }

  getSelectedPermissionLabelForEdit(): string {
    const p = this.datasetData?.permission;
    if (!p) return 'Select permission';
    const normalized = this.normalizeEditPermission(p);
    return normalized === 'Open' ? 'Public' : 'Restricted';
  }

  /**
   * Populate data from assetData input (for editable mode)
   */
  private populateFromAssetData(assetData: any): void {
    console.log('PREVIEW-METADATA: populateFromAssetData - assetData:', assetData);

    const isModel = this.assetType === 'model' ||
      (assetData.type && Array.isArray(assetData.type) && assetData.type.some((t: string) => t.includes('AiModel'))) ||
      (assetData.type && typeof assetData.type === 'string' && assetData.type.includes('AiModel'));

    console.log('PREVIEW-METADATA: populateFromAssetData - isModel:', isModel);

    const permission = this.normalizeEditPermission(
      assetData.permission ?? assetData.accessPolicy ?? assetData.access_policy ?? 'OPEN'
    );

    this.datasetData = {
      title: assetData.name || assetData.label || assetData.title || '',
      permission,
      metadataOption: 'enter'
    };

    console.log('PREVIEW-METADATA: populateFromAssetData - datasetData:', this.datasetData);

    // Parse year range if it's a string
    let yearRange = assetData.yearRange || assetData.year_range || null;
    console.log('PREVIEW-METADATA: populateFromAssetData - yearRange (raw):', yearRange);

    if (typeof yearRange === 'string' && yearRange.includes('-')) {
      const parts = yearRange.split('-').map((p: string) => p.trim());
      if (parts.length === 2) {
        yearRange = {
          startYear: parseInt(parts[0]) || null,
          endYear: parseInt(parts[1]) || null
        };
      }
    }
    console.log('PREVIEW-METADATA: populateFromAssetData - yearRange (parsed):', yearRange);

    const rawResourceServer = assetData.resourceServer ?? assetData.resource_server ?? '';
    console.log('PREVIEW-METADATA: populateFromAssetData - rawResourceServer:', rawResourceServer);
    const resourceServerValue = this.normalizeResourceServerValue(rawResourceServer);
    console.log('PREVIEW-METADATA: populateFromAssetData - resourceServerValue (normalized):', resourceServerValue);

    this.parsedMetadata = {
      shortDescription: assetData.shortDescription || assetData.short_description || '',
      longDescription: assetData.description || assetData.longDescription || '',
      license: assetData.license || 'Open Government License, India',
      otherLicense: assetData.otherLicense || assetData.other_license || '',
      resourceServer: resourceServerValue,
      uploadFrequency: assetData.uploadFrequency || assetData.upload_frequency || 'OneTime',
      yearRange: yearRange,
      geoCoverage: assetData.geoCoverage || assetData.geo_coverage || '',
      industry: assetData.industry || 'Agriculture'
    };

    console.log('PREVIEW-METADATA: populateFromAssetData - parsedMetadata:', this.parsedMetadata);

    const rawMediaUrl =
      assetData.mediaURL || assetData.media_url || assetData.mediaUrl || assetData.sampleDataUrl || '';
    this.parsedAdditionalInfo = {
      datasetType: isModel
        ? (assetData.modelType || assetData.model_type || assetData.datasetType || '')
        : (assetData.dataBankType || assetData.datasetType || 'Structured'),
      fileFormat: assetData.fileFormat || assetData.file_format || 'csv',
      tags: Array.isArray(assetData.tags) ? assetData.tags : (assetData.tags ? [assetData.tags] : []),
      sampleDataUrl: this.normalizeMediaUrlForDisplay(rawMediaUrl),
      dataDescriptors: this.extractDescriptorsFromSource(assetData)
    };

    console.log('PREVIEW-METADATA: populateFromAssetData - parsedAdditionalInfo:', this.parsedAdditionalInfo);

    // Cache descriptors list (isolated copies for display)
    this.dataDescriptorsList = this.cloneDescriptorRows(this.parsedAdditionalInfo.dataDescriptors || []);
  }

  /**
   * Initialize form groups with parsed data
   */
  private initializeForms(): void {
    console.log('PREVIEW-METADATA: initializeForms - START');
    console.log('PREVIEW-METADATA: initializeForms - editableMode:', this.editableMode);
    console.log('PREVIEW-METADATA: initializeForms - assetType:', this.assetType);
    console.log('PREVIEW-METADATA: initializeForms - isModel():', this.isModel());
    console.log('PREVIEW-METADATA: initializeForms - parsedMetadata:', this.parsedMetadata);
    console.log('PREVIEW-METADATA: initializeForms - parsedAdditionalInfo:', this.parsedAdditionalInfo);

    const isDisabled = !this.editableMode;
    const lockTypeAndFormat = this.allowSaveWithoutDirty;
    const isModelAsset = this.isModel();

    // Base validators for common fields
    const validators = this.editableMode ? [Validators.required] : [];
    const shortDescValidators = this.editableMode ? [Validators.required, Validators.minLength(50), Validators.maxLength(200)] : [];
    const longDescValidators = this.editableMode ? [Validators.required, Validators.minLength(50), Validators.maxLength(2000)] : [];

    // Dataset-only validators (not required for models)
    const datasetOnlyValidators = this.editableMode && !isModelAsset ? [Validators.required] : [];
    const modelTypeValidators = this.editableMode && isModelAsset ? [Validators.required] : [];

    console.log('PREVIEW-METADATA: initializeForms - isModelAsset:', isModelAsset);
    console.log('PREVIEW-METADATA: initializeForms - datasetOnlyValidators:', datasetOnlyValidators);

    // Initialize metadata form
    this.metadataForm = this.fb.group({
      shortDescription: [{ value: this.parsedMetadata.shortDescription || '', disabled: isDisabled }, shortDescValidators],
      longDescription: [{ value: this.parsedMetadata.longDescription || '', disabled: isDisabled }, longDescValidators],
      license: [{ value: this.parsedMetadata.license || '', disabled: isDisabled }, validators],
      otherLicense: [{ value: this.parsedMetadata.otherLicense || '', disabled: isDisabled }],
      resourceServer: [{ value: this.parsedMetadata.resourceServer || '', disabled: isDisabled }, datasetOnlyValidators],
      uploadFrequency: [{ value: this.parsedMetadata.uploadFrequency || '', disabled: isDisabled }, validators],
      yearRange: [{ value: this.parsedMetadata.yearRange || null, disabled: isDisabled }, datasetOnlyValidators],
      geoCoverage: [{ value: this.parsedMetadata.geoCoverage || '', disabled: isDisabled }, datasetOnlyValidators],
      industry: [{ value: this.parsedMetadata.industry || '', disabled: isDisabled }, validators]
    });

    console.log('PREVIEW-METADATA: initializeForms - metadataForm created');
    console.log('PREVIEW-METADATA: initializeForms - metadataForm.value:', this.metadataForm.value);
    console.log('PREVIEW-METADATA: initializeForms - metadataForm.valid:', this.metadataForm.valid);
    console.log('PREVIEW-METADATA: initializeForms - metadataForm.errors:', this.metadataForm.errors);

    // Initialize additional info form
    const enableDataSourceInPreview =
      !this.editableMode && this.isDataSourceUrlEnabled();
    const sampleDataUrlDisabled = this.editableMode ? true : !enableDataSourceInPreview;

    this.additionalInfoForm = this.fb.group({
      datasetType: [{ value: this.parsedAdditionalInfo.datasetType || '', disabled: isDisabled }, isModelAsset ? modelTypeValidators : datasetOnlyValidators],
      fileFormat: [{ value: this.parsedAdditionalInfo.fileFormat || '', disabled: isDisabled }, validators],
      tags: [{ value: this.parsedAdditionalInfo.tags || [], disabled: isDisabled }, validators],
      sampleDataUrl: [
        { value: this.parsedAdditionalInfo.sampleDataUrl || '', disabled: sampleDataUrlDisabled },
        [this.optionalUrlPatternValidator],
      ],
      dataDescriptors: [{ value: this.cloneDescriptorRows(this.parsedAdditionalInfo.dataDescriptors || []), disabled: isDisabled }]
    });

    if (!this.isDataSourceUrlEnabled()) {
      this.additionalInfoForm.patchValue({ sampleDataUrl: '' }, { emitEvent: false });
    }

    console.log('PREVIEW-METADATA: initializeForms - additionalInfoForm created');
    console.log('PREVIEW-METADATA: initializeForms - additionalInfoForm.value:', this.additionalInfoForm.value);
    console.log('PREVIEW-METADATA: initializeForms - additionalInfoForm.valid:', this.additionalInfoForm.valid);
    console.log('PREVIEW-METADATA: initializeForms - additionalInfoForm.errors:', this.additionalInfoForm.errors);

    // Ensure dataDescriptorsList stays in sync with parsedAdditionalInfo.
    // parseStorageDataSync / populateFromAssetData already set dataDescriptorsList
    // with a single direct pass; only fall back here if it's still empty.
    if (!this.dataDescriptorsList || this.dataDescriptorsList.length === 0) {
      this.dataDescriptorsList = this.cloneDescriptorRows(
        Array.isArray(this.parsedAdditionalInfo?.dataDescriptors)
          ? this.parsedAdditionalInfo.dataDescriptors
          : this.extractDescriptorsFromSource(this.parsedAdditionalInfo || {}),
      );
    }

    // Update file format options based on dataset type
    if (this.parsedAdditionalInfo.datasetType) {
      this.updateFileFormatOptions(this.parsedAdditionalInfo.datasetType);
    }

    console.log('PREVIEW-METADATA: initializeForms - END');
  }

  /**
   * Update file format options based on dataset type (or model formats when isModel).
   */
  private updateFileFormatOptions(datasetType: string): void {
    this.fileFormatOptions = this.isModel()
      ? this.modelFileFormats
      : (this.fileFormatMap[datasetType] || []);
  }

  /** Align form value with dropdown option casing (e.g. csv → CSV). */
  private syncFileFormatWithOptions(): void {
    const opts = this.isModel() ? this.modelFileFormats : this.fileFormatOptions;
    if (!opts?.length || !this.additionalInfoForm) {
      return;
    }
    const raw =
      this.parsedAdditionalInfo?.fileFormat ??
      this.additionalInfoForm.get('fileFormat')?.value ??
      '';
    const resolved = this.fileFormatConfigService.resolveFileFormatForForm(
      String(raw),
      opts
    );
    if (!resolved) {
      return;
    }
    if (this.parsedAdditionalInfo) {
      this.parsedAdditionalInfo.fileFormat = resolved;
    }
    const ctrl = this.additionalInfoForm.get('fileFormat');
    if (ctrl && ctrl.value !== resolved) {
      ctrl.setValue(resolved, { emitEvent: false });
    }
  }

  /** Whether any form has been modified (for Save Changes button in editable mode). */
  get isFormDirty(): boolean {
    return !!(this.metadataForm?.dirty || this.additionalInfoForm?.dirty);
  }

  /** True when modify-modal values differ from the snapshot taken on open. */
  get hasUnsavedChanges(): boolean {
    if (!this.editableMode || !this.initialModifySnapshot) {
      return false;
    }
    return this.getModifyStateSnapshot() !== this.initialModifySnapshot;
  }

  private getModifyStateSnapshot(): string {
    const metadata = this.metadataForm?.getRawValue?.() ?? {};
    const additional = this.additionalInfoForm?.getRawValue?.() ?? {};
    return JSON.stringify({
      metadata,
      additional,
      permission: this.datasetData?.permission ?? null,
    });
  }

  private captureModifySnapshot(): void {
    if (!this.editableMode || !this.metadataForm || !this.additionalInfoForm) {
      this.initialModifySnapshot = null;
      return;
    }
    this.initialModifySnapshot = this.getModifyStateSnapshot();
  }

  /**
   * Check if current flow is for AI Model
   */
  isModel(): boolean {
    if (this.assetType) {
      return this.assetType === 'model';
    }
    return this.router.url.includes('/add-model/');
  }

  /**
   * Save is enabled only after the user changes metadata in editable (modify) mode.
   */
  get isSaveButtonDisabled(): boolean {
    if (!this.editableMode) {
      return true;
    }
    return !this.hasUnsavedChanges;
  }

  /**
   * Handle Save Changes button click (wrapper with logging)
   */
  onSaveChangesClick(): void {
    console.log('=== PREVIEW-METADATA: Save Changes button CLICKED ===');
    console.log('PREVIEW-METADATA: Button click event detected');
    console.log('PREVIEW-METADATA: Button disabled state:', this.isSaveButtonDisabled);
    this.onSubmit();
  }

  /**
   * Handle form submit (for editable mode)
   */
  onSubmit(): void {
    console.log('=== PREVIEW-METADATA: onSubmit CALLED ===');
    console.log('PREVIEW-METADATA: editableMode:', this.editableMode);
    console.log('PREVIEW-METADATA: allowSaveWithoutDirty:', this.allowSaveWithoutDirty);
    console.log('PREVIEW-METADATA: metadataForm exists:', !!this.metadataForm);
    console.log('PREVIEW-METADATA: additionalInfoForm exists:', !!this.additionalInfoForm);

    if (!this.editableMode) {
      console.warn('PREVIEW-METADATA: Not in editable mode - returning');
      return;
    }
    if (!this.hasUnsavedChanges) {
      return;
    }
    if (!this.metadataForm || !this.additionalInfoForm) {
      console.warn('PREVIEW-METADATA: Forms not initialized - returning');
      return;
    }

    console.log('PREVIEW-METADATA: metadataForm.valid:', this.metadataForm.valid);
    console.log('PREVIEW-METADATA: metadataForm.invalid:', this.metadataForm.invalid);
    console.log('PREVIEW-METADATA: metadataForm.errors:', this.metadataForm.errors);
    console.log('PREVIEW-METADATA: additionalInfoForm.valid:', this.additionalInfoForm.valid);
    console.log('PREVIEW-METADATA: additionalInfoForm.invalid:', this.additionalInfoForm.invalid);
    console.log('PREVIEW-METADATA: additionalInfoForm.errors:', this.additionalInfoForm.errors);

    // For modify mode (allowSaveWithoutDirty = true), allow submission even with validation errors
    // This is needed for existing assets that might have data not meeting current validation rules
    if (this.metadataForm.invalid || this.additionalInfoForm.invalid) {
      console.warn('PREVIEW-METADATA: Forms have validation errors');
      // Mark all fields as touched to show validation errors
      Object.keys(this.metadataForm.controls).forEach(key => {
        const control = this.metadataForm.get(key);
        control?.markAsTouched();
        if (control?.invalid) {
          console.log(`PREVIEW-METADATA: metadataForm.${key} is invalid:`, control.errors);
        }
      });
      Object.keys(this.additionalInfoForm.controls).forEach(key => {
        const control = this.additionalInfoForm.get(key);
        control?.markAsTouched();
        if (control?.invalid) {
          console.log(`PREVIEW-METADATA: additionalInfoForm.${key} is invalid:`, control.errors);
        }
      });

      // If not in allowSaveWithoutDirty mode, stop here (strict validation for new contributions)
      if (!this.allowSaveWithoutDirty) {
        console.warn('PREVIEW-METADATA: Stopping submission due to validation errors (strict mode)');
        return;
      }
      console.log('PREVIEW-METADATA: Allowing submission despite validation errors (modify mode with allowSaveWithoutDirty=true)');
    }

    const resolvedDescriptors = this.resolvePayloadDescriptors({
      ...this.metadataForm.value,
      ...this.additionalInfoForm.getRawValue(),
    });

    // Combine form values (getRawValue for additionalInfo so read-only sampleDataUrl is preserved on save)
    const formData = {
      ...this.metadataForm.value,
      ...this.additionalInfoForm.getRawValue(),
      dataDescriptors:
        resolvedDescriptors.length > 0
          ? resolvedDescriptors
          : this.additionalInfoForm.getRawValue().dataDescriptors,
      title: this.datasetData?.title || '',
      permission: this.datasetData?.permission || 'Open'
    };

    console.log('PREVIEW-METADATA: Emitting formSubmit with formData:', formData);
    this.formSubmit.emit(formData);
    console.log('=== PREVIEW-METADATA: formSubmit emitted ===');
  }

  /**
   * Handle form cancel (for editable mode)
   */
  onCancel(): void {
    if (!this.editableMode) {
      return;
    }
    this.formCancel.emit();
  }

  /**
   * Add tag (for editable mode)
   */
  addTag(): void {
    if (!this.editableMode) {
      return;
    }
    const tag = this.sanitizeTagInput(this.tagInput.trim());
    this.tagInput = tag;
    if (!tag || !this.isValidTagValue(tag)) {
      return;
    }
    const currentTags = this.getTags();
    // Check if tag already exists (case-insensitive)
    if (!currentTags.some(t => t.toLowerCase() === tag.toLowerCase())) {
      currentTags.push(tag);
      this.additionalInfoForm.patchValue({ tags: currentTags });
      this.additionalInfoForm.get('tags')?.markAsTouched();
      this.additionalInfoForm.markAsDirty();
      this.tagInput = '';
    }
  }

  /**
   * Remove tag (for editable mode)
   */
  removeTag(tagToRemove: string): void {
    if (!this.editableMode) {
      return;
    }
    const currentTags = this.getTags();
    const updatedTags = currentTags.filter(tag => tag !== tagToRemove);
    this.additionalInfoForm.patchValue({ tags: updatedTags });
    this.additionalInfoForm.get('tags')?.markAsTouched();
    this.additionalInfoForm.markAsDirty();
  }

  /**
   * Handle tag input keydown (for editable mode)
   */
  onTagInputKeydown(event: KeyboardEvent): void {
    if (!this.editableMode) {
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addTag();
      return;
    }
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    if (event.key.length > 1) {
      return;
    }
    if (!/^[a-zA-Z0-9]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  onTagInputChange(): void {
    if (!this.editableMode) {
      return;
    }
    const sanitized = this.sanitizeTagInput(this.tagInput);
    if (sanitized !== this.tagInput) {
      this.tagInput = sanitized;
    }
  }

  onTagInputPaste(event: ClipboardEvent): void {
    if (!this.editableMode) {
      return;
    }
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') ?? '';
    this.tagInput = this.sanitizeTagInput(this.tagInput + pasted);
  }

  canAddTag(): boolean {
    const tag = this.tagInput.trim();
    return tag.length > 0 && this.isValidTagValue(tag);
  }

  private sanitizeTagInput(value: string): string {
    return value.replace(/[^a-zA-Z0-9]/g, '');
  }

  private isValidTagValue(tag: string): boolean {
    return /^[a-zA-Z0-9]+$/.test(tag);
  }

  /** Data Source URL only when permission is Public/Open (models and datasets). */
  isDataSourceUrlEnabled(): boolean {
    return this.isPublicModelPermission(this.datasetData?.permission);
  }

  hasRedirectUrl(): boolean {
    const url = this.additionalInfoForm?.get('sampleDataUrl')?.value;
    return !!(url && String(url).trim().length > 0);
  }

  shouldDisableStepClick = (stepIndex: number): boolean => {
    return stepIndex === 3 && this.hasRedirectUrl();
  };

  optionalUrlPatternValidator = (control: AbstractControl): { [key: string]: unknown } | null => {
    const value = control.value;
    if (!value || String(value).trim() === '') {
      return null;
    }
    const urlPattern = /^https?:\/\/.+/;
    return urlPattern.test(String(value)) ? null : { pattern: { value: control.value } };
  };

  private setupSampleDataUrlListener(): void {
    if (this.editableMode || !this.additionalInfoForm) {
      return;
    }
    this.additionalInfoForm.get('sampleDataUrl')?.valueChanges.subscribe(() => {
      this.savePreviewFormDataToStorage();
      this.cdr.detectChanges();
    });
  }

  private savePreviewFormDataToStorage(): void {
    if (!this.additionalInfoForm) {
      return;
    }
    const localStorageKey = this.getLocalStorageKey();
    const existingData = localStorage.getItem(localStorageKey);
    const existingFormData = existingData ? JSON.parse(existingData) : {};
    const sampleDataUrl = !this.isDataSourceUrlEnabled()
      ? ''
      : (this.additionalInfoForm.get('sampleDataUrl')?.value ?? '');
    const updatedData = {
      ...existingFormData,
      ...this.additionalInfoForm.getRawValue(),
      sampleDataUrl,
    };
    localStorage.setItem(localStorageKey, JSON.stringify(updatedData));
  }

  closeSuccessModal(): void {
    this.isSuccessModalOpen = false;
  }

  onViewItem(): void {
    this.closeSuccessModal();
    if (this.createdItemId) {
      const route = this.isModel() ? ['/model', this.createdItemId] : ['/dataset', this.createdItemId];
      this.router.navigate(route);
    } else if (this.isModel()) {
      this.router.navigate(['/models']);
    } else {
      this.router.navigate(['/datasets']);
    }
  }

  onCreateNew(): void {
    this.closeSuccessModal();
    const localStorageKey = this.getLocalStorageKey();
    localStorage.removeItem(localStorageKey);
    sessionStorage.removeItem('contributionFlowActive');
    sessionStorage.removeItem('cameFromUploadsStep');
    this.createdItemId = null;

    const currentUrl = this.router.url;
    let navigateRoute: string;
    if (currentUrl.includes('/org-admin/')) {
      navigateRoute = currentUrl.includes('/add-model/')
        ? '/org-admin/add-model'
        : '/org-admin/add-dataset';
    } else {
      navigateRoute = currentUrl.includes('/add-model/')
        ? '/provider/add-contributions/add-model'
        : '/provider/add-contributions/add-dataset';
    }
    this.router.navigateByUrl(navigateRoute, { replaceUrl: true });
  }

  private isPublicModelPermission(permission: string | null | undefined): boolean {
    const p = String(permission ?? 'Open').trim();
    return p === 'Open' || p.toUpperCase() === 'PUBLIC';
  }

  /**
   * Get label for license
   */
  getLicenseLabel(value: string): string {
    const option = this.licenseOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  /**
   * Get label for industry
   */
  getIndustryLabel(value: string): string {
    const option = this.industryOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  /**
   * Get label for upload frequency
   */
  getUploadFrequencyLabel(value: string): string {
    const option = this.uploadFrequencyOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  }

  /**
   * Get label for dataset type
   */
  getDatasetTypeLabel(value: string): string {
    return getDatasetTypeDisplayLabel(value) || value;
  }

  /**
   * Get label for file format
   */
  getFileFormatLabel(value: string): string {
    const option = this.fileFormatOptions.find(
      (opt) =>
        opt.value === value ||
        opt.value.toLowerCase() === String(value).toLowerCase()
    );
    return option ? option.label : value;
  }

  /**
   * Get resource server display
   */
  getResourceServerDisplay(value: string): string {
    if (!value) {
      return '';
    }
    if (value.toUpperCase() === ProviderPreviewMetadataComponent.FILE_RESOURCE_SERVER_VALUE) {
      return 'FILE';
    }
    const option = this.resourceServerOptions.find((opt) => opt.value === value);
    return option ? option.displayLabel : value;
  }

  // --- Editable mode dropdowns: Resource Server ---
  toggleResourceServerDropdown(): void {
    if (!this.editableMode) return;
    const willOpen = !this.isResourceServerDropdownOpen;
    this.closeAllEditDropdowns();
    this.isResourceServerDropdownOpen = willOpen;
  }

  selectResourceServer(option: { value: string; label: string; displayLabel: string; name: string; url: string }): void {
    this.metadataForm.patchValue({ resourceServer: option.value });
    this.metadataForm.get('resourceServer')?.markAsTouched();
    this.metadataForm.markAsDirty();
    this.isResourceServerDropdownOpen = false;
  }

  getSelectedResourceServerLabel(): string {
    const v =
      this.metadataForm?.get('resourceServer')?.value ??
      this.metadataForm?.getRawValue?.()?.resourceServer ??
      this.parsedMetadata?.resourceServer;
    const display = this.getResourceServerDisplay(String(v ?? ''));
    if (display) {
      return display;
    }
    const raw = this.assetData?.resourceServer ?? this.assetData?.resource_server;
    const entry = this.extractResourceServerEntry(raw);
    if (entry && this.isFileResourceServerEntry(entry)) {
      return 'FILE';
    }
    const name = entry ? String(entry['name'] ?? '').trim() : '';
    return name || 'Select resource server';
  }

  isResourceServerSelected(value: string): boolean {
    return this.metadataForm.get('resourceServer')?.value === value;
  }

  // --- Editable mode dropdowns: Upload Frequency ---
  toggleUploadFrequencyDropdown(): void {
    if (!this.editableMode) return;
    const willOpen = !this.isUploadFrequencyDropdownOpen;
    this.closeAllEditDropdowns();
    this.isUploadFrequencyDropdownOpen = willOpen;
  }

  selectUploadFrequency(option: { value: string; label: string }): void {
    this.metadataForm.patchValue({ uploadFrequency: option.value });
    this.metadataForm.get('uploadFrequency')?.markAsTouched();
    this.metadataForm.markAsDirty();
    this.isUploadFrequencyDropdownOpen = false;
  }

  getSelectedUploadFrequencyLabel(): string {
    const v = this.metadataForm.get('uploadFrequency')?.value;
    return this.getUploadFrequencyLabel(v) || 'Select upload frequency';
  }

  isUploadFrequencySelected(value: string): boolean {
    return this.metadataForm.get('uploadFrequency')?.value === value;
  }

  // --- Editable mode dropdowns: License ---
  toggleLicenseDropdown(): void {
    if (!this.editableMode) return;
    const willOpen = !this.isLicenseDropdownOpen;
    this.closeAllEditDropdowns();
    this.isLicenseDropdownOpen = willOpen;
  }

  selectLicense(option: { value: string; label: string }): void {
    this.metadataForm.patchValue({ license: option.value });
    this.metadataForm.get('license')?.markAsTouched();
    this.metadataForm.markAsDirty();
    this.isLicenseDropdownOpen = false;
  }

  getSelectedLicenseLabel(): string {
    const v = this.metadataForm.get('license')?.value;
    if (!v) return 'Select license';
    return v === 'Other' ? 'Other' : (this.getLicenseLabel(v) || 'Select license');
  }

  isLicenseSelected(value: string): boolean {
    return this.metadataForm.get('license')?.value === value;
  }

  // --- Editable mode dropdowns: Industry ---
  toggleIndustryDropdown(): void {
    if (!this.editableMode) return;
    const willOpen = !this.isIndustryDropdownOpen;
    this.closeAllEditDropdowns();
    this.isIndustryDropdownOpen = willOpen;
  }

  selectIndustry(option: { value: string; label: string }): void {
    this.metadataForm.patchValue({ industry: option.value });
    this.metadataForm.get('industry')?.markAsTouched();
    this.metadataForm.markAsDirty();
    this.isIndustryDropdownOpen = false;
  }

  getSelectedIndustryLabel(): string {
    const v = this.metadataForm.get('industry')?.value;
    return this.getIndustryLabel(v) || 'Select industry';
  }

  isIndustrySelected(value: string): boolean {
    return this.metadataForm.get('industry')?.value === value;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.editableMode) return;
    const target = event.target as HTMLElement;
    if (target?.closest?.('.custom-dropdown-wrapper')) return;
    this.closeAllEditDropdowns();
  }

  @HostListener('document:focusin', ['$event'])
  onDocumentFocusIn(event: FocusEvent): void {
    if (!this.editableMode) return;
    const target = event.target as HTMLElement;
    if (target?.closest?.('.custom-dropdown-wrapper')) return;
    this.closeAllEditDropdowns();
  }

  // --- Editable mode dropdowns: Dataset Type ---
  toggleDatasetTypeDropdown(): void {
    if (!this.editableMode || this.allowSaveWithoutDirty) return;
    const willOpen = !this.isDatasetTypeDropdownOpen;
    this.closeAllEditDropdowns();
    this.isDatasetTypeDropdownOpen = willOpen;
  }

  selectDatasetType(option: { value: string; label: string }): void {
    this.additionalInfoForm.patchValue({ datasetType: option.value });
    this.additionalInfoForm.get('datasetType')?.markAsTouched();
    this.additionalInfoForm.markAsDirty();
    if (this.isModel()) {
      this.updateFileFormatOptions('');
    } else {
      this.updateFileFormatOptions(option.value);
    }
    this.isDatasetTypeDropdownOpen = false;
  }

  getSelectedDatasetTypeLabel(): string {
    const v = this.additionalInfoForm.get('datasetType')?.value;
    if (this.isModel()) {
      return this.getDatasetTypeLabel(v) || 'Select model type';
    }
    return this.getDatasetTypeLabel(v) || 'Select dataset type';
  }

  isDatasetTypeSelected(value: string): boolean {
    return this.additionalInfoForm.get('datasetType')?.value === value;
  }

  // --- Editable mode dropdowns: File Format ---
  toggleFileFormatDropdown(): void {
    if (!this.editableMode || this.allowSaveWithoutDirty) return;
    const willOpen = !this.isFileFormatDropdownOpen;
    this.closeAllEditDropdowns();
    this.isFileFormatDropdownOpen = willOpen;
  }

  selectFileFormat(option: { value: string; label: string }): void {
    this.additionalInfoForm.patchValue({ fileFormat: option.value });
    this.additionalInfoForm.get('fileFormat')?.markAsTouched();
    this.additionalInfoForm.markAsDirty();
    this.isFileFormatDropdownOpen = false;
  }

  getSelectedFileFormatLabel(): string {
    const v =
      this.additionalInfoForm.get('fileFormat')?.value ??
      this.additionalInfoForm.getRawValue?.()?.fileFormat ??
      this.parsedAdditionalInfo?.fileFormat;
    return this.getFileFormatLabel(v) || 'Select file format';
  }

  isFileFormatSelected(value: string): boolean {
    const cur = this.additionalInfoForm.get('fileFormat')?.value;
    return String(cur).toLowerCase() === String(value).toLowerCase();
  }

  /**
   * Get tags array
   */
  getTags(): string[] {
    return this.additionalInfoForm.get('tags')?.value || [];
  }

  /** Highlight existing tag chip when input text matches it (same as usecase add tags). */
  isTagDuplicate(tag: string): boolean {
    const input = (this.tagInput || '').trim();
    if (!input) return false;
    return tag.toLowerCase() === input.toLowerCase();
  }

  /**
   * Get data descriptors for display and payload.
   * Uses getRawValue() so disabled controls (e.g. in preview mode) are included.
   */
  getDataDescriptors(): AssetDescriptorInput[] {
    if (!this.additionalInfoForm) return [];
    const raw = this.additionalInfoForm.getRawValue?.();
    const descriptors = raw?.dataDescriptors ?? this.additionalInfoForm.get('dataDescriptors')?.value;
    if (!Array.isArray(descriptors)) return [];
    const out: AssetDescriptorInput[] = [];
    descriptors.forEach((desc: any) => {
      const normalized = this.normalizeDataDescriptor(desc);
      if (normalized) out.push(normalized);
    });
    return out;
  }

  /**
   * Check if data descriptors exist and are valid
   */
  hasDataDescriptors(): boolean {
    // Use cached list for better performance and reliability
    return Array.isArray(this.dataDescriptorsList) && this.dataDescriptorsList.length > 0;
  }

  /**
   * Check if other license is selected
   */
  isOtherLicenseSelected(): boolean {
    return this.metadataForm.get('license')?.value === 'Other';
  }

  /**
   * Format year range for display
   */
  formatYearRange(yearRange: any): string {
    if (!yearRange) return '';
    if (typeof yearRange === 'object' && yearRange.startYear && yearRange.endYear) {
      return `${yearRange.startYear} - ${yearRange.endYear}`;
    }
    return String(yearRange);
  }

  /**
   * Check if data source field has a value
   */
  hasDataSource(): boolean {
    const value = this.additionalInfoForm.get('sampleDataUrl')?.value;
    return value && value.trim().length > 0;
  }

  ngAfterViewInit(): void {
    // Update step 3 with template reference after view is initialized.
    // static: false is required because #step3Template is inside *ngIf="!editableMode".
    if (this.step3Template && this.stepperSteps?.length > 2) {
      this.stepperSteps[2] = { ...this.stepperSteps[2], content: this.step3Template };
      this.cdr.detectChanges();
    }
  }

  onStepChange(stepIndex: number): void {
    if (stepIndex === 3 && this.hasRedirectUrl()) {
      return;
    }

    this.savePreviewFormDataToStorage();

    const currentUrl = this.router.url;
    let routeBase: string;

    if (currentUrl.includes('/add-dataset/')) {
      routeBase = 'provider/add-contributions/add-dataset';
    } else if (currentUrl.includes('/add-model/')) {
      routeBase = 'provider/add-contributions/add-model';
    } else {
      // Default to add-dataset if we can't determine from URL
      routeBase = 'provider/add-contributions/add-dataset';
    }

    const routes = [
      routeBase,
      `${routeBase}/upload-metadata`,
      `${routeBase}/preview-metadata`,
      `${routeBase}/uploads`
    ];

    if (stepIndex >= 0 && stepIndex < routes.length) {
      this.router.navigate([routes[stepIndex]]);
    }
  }

  async onNext(): Promise<void> {
    // Check if already creating to prevent duplicate calls
    if (this.isCreatingItem) {
      console.warn('Already creating item, skipping duplicate call');
      return;
    }

    if (this.isModel() && this.hasRedirectUrl()) {
      const sampleDataUrlControl = this.additionalInfoForm?.get('sampleDataUrl');
      if (sampleDataUrlControl?.invalid) {
        sampleDataUrlControl.markAsTouched();
        return;
      }
    }

    try {
      this.isCreatingItem = true;
      console.log('Creating item from Preview Metadata step...');

      this.savePreviewFormDataToStorage();

      // Get form data from localStorage
      const localStorageKey = this.getLocalStorageKey();
      const existingData = localStorage.getItem(localStorageKey);
      const formDataFromStorage = existingData ? JSON.parse(existingData) : {};

      // Merge current form values (what's displayed in Preview) so payload matches the form
      const metadataValues = this.metadataForm?.getRawValue?.() || {};
      const additionalValues = this.additionalInfoForm?.getRawValue?.() || {};
      const formData = {
        ...formDataFromStorage,
        ...metadataValues,
        ...additionalValues,
        title: this.datasetData?.title ?? formDataFromStorage.title,
        permission: this.datasetData?.permission ?? formDataFromStorage.permission
      };

      if (!this.isDataSourceUrlEnabled()) {
        formData.sampleDataUrl = '';
      }

      // Check if item already exists
      let itemId: string | null = formData.itemId || formData.id || formData.item_id || null;

      // If item doesn't exist, create it
      if (!itemId) {
        console.log('No itemId found; creating item...');
        const itemPayload = await this.buildCreateAssetPayload(formData);
        const response = await this.catalogueApiService.createAssetItem(itemPayload);

        // Extract item ID from response
        const createdItemId =
          response?.result?.itemId ||
          response?.result?.id ||
          response?.data?.id ||
          response?.id ||
          response?.result?.data?.id;

        if (createdItemId) {
          itemId = createdItemId;
          console.log('Item created successfully with ID:', createdItemId);

          // Store item ID in localStorage
          formData.itemId = createdItemId;
          formData.id = createdItemId;
          localStorage.setItem(localStorageKey, JSON.stringify(formData));
          this.createdItemId = createdItemId;
        } else {
          console.warn('Item ID not found in create response:', response);
          this.toasterService.error('Failed to create dataset. Please try again.');
          return;
        }
      } else {
        console.log('Item already exists with ID:', itemId);
        this.createdItemId = itemId;
      }

      if (this.hasRedirectUrl()) {
        this.isSuccessModalOpen = true;
      } else {
        this.proceed();
      }
    } catch (error) {
      console.error('Error creating item:', error);
      const isModel = this.isModel();
      this.toasterService.error(
        isModel
          ? 'Unable to create a Model, Please try again later !!'
          : 'Unable to create a Dataset, Please try again later !!'
      );
    } finally {
      this.isCreatingItem = false;
    }
  }

  onPrevious(): void {
    // Navigate to previous step (upload-metadata)
    this.goBack();
  }

  goBack(): void {
    this.router.navigate(['../upload-metadata'], { relativeTo: this.route });
  }

  proceed(): void {
    // Navigate to uploads (Step 4: Upload Data)
    this.router.navigate(['../uploads'], { relativeTo: this.route });
  }

  private resolvePayloadDescriptors(formData: Record<string, any>): AssetDescriptorInput[] {
    const resolved = resolveModifyAssetDescriptors(formData, { assetPreviewData: this.assetData });
    if (resolved.length > 0) {
      return resolved;
    }

    if (formData['jsonSchema']) {
      try {
        const parsedSchema =
          typeof formData['jsonSchema'] === 'string'
            ? JSON.parse(formData['jsonSchema'])
            : formData['jsonSchema'];
        const fromSchema = this.extractDescriptorsFromSource(parsedSchema);
        if (fromSchema.length > 0) {
          return fromSchema;
        }
      } catch {
        /* ignore */
      }
    }

    if (this.dataDescriptorsList.length > 0) {
      return [...this.dataDescriptorsList];
    }

    return [];
  }

  private async buildCreateAssetPayload(formData: any): Promise<any> {
    const isModel = this.router.url.includes('/add-model/');
    return buildAssetCataloguePayloadWithAuth(this.authApiService, {
      formData,
      isModel,
      dataDescriptors: this.resolvePayloadDescriptors(formData),
      includeModelDataSourceUrl: this.isDataSourceUrlEnabled(),
      defaultModelMediaUrl: 'https://iudx.io/',
    });
  }

  /** UI only: show "Public" for "Open", API still uses OPEN */
  getPermissionDisplayLabel(permission: string | null | undefined): string {
    return !permission ? '' : permission === 'Open' ? 'Public' : permission;
  }
}
